<template>
  <span ref="marker" hidden />
</template>

<script setup lang="ts">
/**
 * Gives each option Ant's Select announces its true position out of the
 * options shown. For assistive technology Ant renders a hidden listbox holding
 * only the active option and its two neighbours, so without this VoiceOver
 * reads every option as "2 of 3". Render it in the Select's `dropdownRender`
 * slot, where Ant's own list of shown options can be injected.
 *
 * Relies on ant-design-vue's internal select context and the
 * `${id}_list_${index}` ids of its hidden options. Remove it once Ant sets
 * aria-posinset and aria-setsize itself, and recheck when upgrading Ant.
 * @see node_modules/ant-design-vue/es/vc-select/OptionList.js renderItem (ant-design-vue 4.2.6, 2026-10-13)
 */
import { useMutationObserver } from '@vueuse/core';
import type { RawValueType } from 'ant-design-vue/es/vc-select/BaseSelect';
import useSelectProps from 'ant-design-vue/es/vc-select/SelectContext';
import { computed, useTemplateRef, watchEffect } from 'vue';

const { selectId, isSkippingSelected = false } = defineProps<{
  selectId: string;
  /** Selected options are hidden from the dropdown, so they do not count. */
  isSkippingSelected?: boolean;
}>();

const selectContext = useSelectProps();
const marker = useTemplateRef<HTMLElement>('marker');
const dropdown = computed(
  () => marker.value?.closest<HTMLElement>('.ant-select-dropdown') ?? null,
);

function isHiddenSelected(value: RawValueType | undefined) {
  return (
    isSkippingSelected &&
    value !== undefined &&
    !!selectContext.rawValues?.has(value)
  );
}

const shownOptions = computed(() =>
  (selectContext.flattenOptions ?? []).filter(
    (item) => !item.group && !isHiddenSelected(item.value),
  ),
);

function setOrRemove(element: Element, name: string, value: string | null) {
  if (value === null) element.removeAttribute(name);
  else if (element.getAttribute(name) !== value)
    element.setAttribute(name, value);
}

function applyPositions() {
  const idPrefix = `${selectId}_list_`;
  const listbox = document.getElementById(`${selectId}_list`);
  listbox?.querySelectorAll('[role="option"]').forEach((option) => {
    const item =
      selectContext.flattenOptions?.[Number(option.id.slice(idPrefix.length))];
    const position = item ? shownOptions.value.indexOf(item) + 1 : 0;
    setOrRemove(option, 'aria-posinset', position ? String(position) : null);
    setOrRemove(
      option,
      'aria-setsize',
      position ? String(shownOptions.value.length) : null,
    );
  });
}

useMutationObserver(dropdown, applyPositions, {
  childList: true,
  subtree: true,
});
watchEffect(applyPositions, { flush: 'post' });
</script>
